import React, { useRef } from "react";
import FlatpickrBase from "react-flatpickr";
import { Bulgarian } from "flatpickr/dist/l10n/bg.js";
import moment from "moment";
import { inputClass, labelClass } from "../../../constants/formStyles.ts";

type DatePickerProps = {
  label?: string;
  onChange: (e: { target: { name: string; value: string } }) => void;
  value: string;
  name: string;
  classNameAdditional?: string;
  disabled?: boolean;
};

const Flatpickr = (FlatpickrBase as any).default ?? FlatpickrBase;

const DatePicker: React.FC<DatePickerProps> = ({
  label = "",
  onChange,
  value,
  name,
  classNameAdditional = "",
  disabled = false,
}) => {
  const fpRef = useRef<any>(null);

  const normalizeSeparators = (val: string) => val.replace(/[.,\s-]/g, "/");
  const validateDate = (val: string) => /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val);

  // Fired by flatpickr when a date is selected via the calendar (desktop or mobile native picker)
  const handleDateChange = (dates: Date[]) => {
    if (dates.length > 0) {
      const formatted = moment(dates[0]).format("DD/MM/YYYY");
      onChange({ target: { name, value: formatted } });
    }
  };

  // Fired on manual keyboard input when the field loses focus.
  // We always read the literal text currently in the input — flatpickr's
  // internal selectedDates can lag behind or hold a stale value from before
  // the user started editing, so it must never be used to decide whether to
  // skip validation (that previously caused partial edits, e.g. changing
  // just the month digits, to be silently dropped).
  //
  // NOTE: we deliberately do NOT call flatpickr instance methods (e.g.
  // setDate) here. react-flatpickr already owns syncing the displayed value
  // from the controlled `value` prop once onChange updates parent state.
  // Calling setDate imperatively raced with that re-sync and caused
  // flatpickr to tear down/reinitialize a second, misconfigured instance
  // (visible as a duplicate calendar icon with the locale/styling reset).
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const typedValue = normalizeSeparators(e.target.value);

    if (!typedValue) {
      onChange({ target: { name, value: moment().format("DD/MM/YYYY") } });
      return;
    }

    if (!validateDate(typedValue)) {
      onChange({ target: { name, value: moment().format("DD/MM/YYYY") } });
      return;
    }

    onChange({ target: { name, value: typedValue } });
  };

  const handleOpen = (_: any, __: any, instance: any) => {
    const cal = instance.calendarContainer as HTMLElement;
    const input = instance.input as HTMLElement;
    if (!cal || !input) return;

    requestAnimationFrame(() => {
      const inputRect = input.getBoundingClientRect();
      const calHeight = cal.offsetHeight || 320;
      const spaceBelow = window.innerHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      cal.style.position = "fixed";
      cal.style.left = `${inputRect.left}px`;
      cal.style.width = `${inputRect.width}px`;
      cal.style.minWidth = "unset";

      if (spaceBelow < calHeight && spaceAbove > spaceBelow) {
        cal.style.removeProperty("top");
        cal.style.bottom = `${window.innerHeight - inputRect.top}px`;
      } else {
        cal.style.removeProperty("bottom");
        cal.style.top = `${inputRect.bottom}px`;
      }
    });
  };

  return (
    <div>
      <div className="relative">
        {label && <label className={labelClass}>{label}</label>}
        <div className="relative overflow-hidden">
          <Flatpickr
            ref={fpRef}
            name={name}
            options={{
              locale: Bulgarian,
              mode: "single",
              monthSelectorType: "dropdown",
              dateFormat: "d/m/Y",
              clickOpens: false,
              closeOnSelect: true,
              allowInput: true,
              prevArrow: '<i class="fa-solid fa-arrow-left"></i>',
              nextArrow: '<i class="fa-solid fa-arrow-right"></i>',
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            className={`form-datepicker ${inputClass} pr-7 ${classNameAdditional}`}
            placeholder="дд/мм/гггг"
            value={value}
            onChange={handleDateChange}
            onBlur={handleBlur}
            onOpen={handleOpen}
            disabled={disabled}
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => fpRef.current?.flatpickr?.toggle()}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 focus:outline-none dark:hover:text-gray-300"
            aria-label="Отвори календар"
          >
            <i className="fa-light fa-calendar-days" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
